import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	ArrowLeft,
	MapPin,
	Briefcase,
	Building2,
	ExternalLink,
	FileText,
	Loader2,
	Star,
	Pencil,
	Check,
	X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@hiring-intelligence/ui';
import { Button } from '@hiring-intelligence/ui';
import { Badge } from '@hiring-intelligence/ui';
import { Input } from '@hiring-intelligence/ui';
import { candidateApi } from '@/services/api';
import { useToast } from '@hiring-intelligence/ui';

const hiringRequestStatusColors: Record<string, string> = {
	draft: 'secondary',
	gathering_criteria: 'secondary',
	criteria_complete: 'default',
	jd_generated: 'default',
	pending_feedback: 'default',
	completed: 'success',
	cancelled: 'destructive'
};

const candidateStatusColors: Record<string, string> = {
	sourced: 'secondary',
	contacted: 'default',
	interviewing: 'default',
	hired: 'success',
	rejected: 'destructive'
};

const formatStatus = (status: string) =>
	status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

function maskEmail(email: string): string {
	const atIndex = email.indexOf('@');
	if (atIndex < 0) return email;
	const local = email.slice(0, atIndex);
	const domain = email.slice(atIndex);
	return `${local.slice(0, 2)}***${domain}`;
}

export default function CandidateDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { toast } = useToast();

	const [editingEmail, setEditingEmail] = useState(false);
	const [emailInput, setEmailInput] = useState('');

	const { data, isLoading } = useQuery({
		queryKey: ['candidate-detail', id],
		queryFn: () => candidateApi.get(id!),
		enabled: !!id
	});

	const updateEmailMutation = useMutation({
		mutationFn: (email: string) => candidateApi.updateEmail(id!, email),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['candidate-detail', id] });
			setEditingEmail(false);
			setEmailInput('');
			toast({ title: 'Email updated' });
		},
		onError: (err: Error) => {
			toast({ title: 'Error', description: err.message, variant: 'destructive' });
		}
	});

	const candidate = data?.data?.candidate;
	const hiringRequests = data?.data?.hiringRequests || [];

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-24">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (!candidate) {
		return (
			<div className="p-4 md:p-8">
				<Button variant="ghost" onClick={() => navigate('/candidates')}>
					<ArrowLeft className="mr-2 h-4 w-4" />
					Back to Candidates
				</Button>
				<div className="flex items-center justify-center py-16">
					<p className="text-muted-foreground">Candidate not found</p>
				</div>
			</div>
		);
	}

	return (
		<div className="p-4 md:p-8 max-w-4xl">
			{/* Back button */}
			<Button variant="ghost" onClick={() => navigate('/candidates')} className="mb-6">
				<ArrowLeft className="mr-2 h-4 w-4" />
				Back to Candidates
			</Button>

			{/* Candidate Info Card */}
			<Card className="mb-6">
				<CardContent className="pt-6">
					<div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
						<div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
							<span className="text-lg font-semibold text-primary">
								{candidate.name?.charAt(0).toUpperCase()}
							</span>
						</div>
						<div className="flex-1 min-w-0">
							<h1 className="text-xl sm:text-2xl font-bold text-center sm:text-left">{candidate.name}</h1>
							{(candidate.currentTitle || candidate.currentCompany) && (
								<div className="flex items-center gap-1 text-muted-foreground mt-1">
									<Briefcase className="h-4 w-4 shrink-0" />
									<span>
										{candidate.currentTitle}
										{candidate.currentCompany ? ` at ${candidate.currentCompany}` : ''}
									</span>
								</div>
							)}
							{candidate.location && (
								<div className="flex items-center gap-1 text-muted-foreground mt-1">
									<MapPin className="h-4 w-4 shrink-0" />
									<span>{candidate.location}</span>
								</div>
							)}
						{candidate.email && !editingEmail && (
							<div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
								<span>✉️ {maskEmail(candidate.email)}</span>
								<Button
									variant="ghost"
									size="sm"
									className="h-6 w-6 p-0"
									onClick={() => { setEditingEmail(true); setEmailInput(''); }}
								>
									<Pencil className="h-3 w-3" />
								</Button>
							</div>
						)}
						{!candidate.email && !editingEmail && (
							<div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
								<span>No email</span>
								<Button
									variant="ghost"
									size="sm"
									className="h-6 w-6 p-0"
									onClick={() => { setEditingEmail(true); setEmailInput(''); }}
								>
									<Pencil className="h-3 w-3" />
								</Button>
							</div>
						)}
						{editingEmail && (
							<div className="flex items-center gap-2 mt-1">
								<Input
									type="email"
									placeholder="Enter email"
									value={emailInput}
									onChange={(e: any) => setEmailInput((e.target as any).value)}
									className="h-7 text-sm max-w-xs"
									autoFocus
								/>
								<Button
									size="sm"
									className="h-7 px-2"
									onClick={() => updateEmailMutation.mutate(emailInput)}
									disabled={updateEmailMutation.isPending || !emailInput.trim()}
								>
									{updateEmailMutation.isPending
										? <Loader2 className="h-3 w-3 animate-spin" />
										: <Check className="h-3 w-3" />}
								</Button>
								<Button
									variant="ghost"
									size="sm"
									className="h-7 px-2"
									onClick={() => { setEditingEmail(false); setEmailInput(''); }}
								>
									<X className="h-3 w-3" />
								</Button>
							</div>
						)}
							<div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-3">
								{candidate.linkedinUrl && (
									<Button
										variant="outline"
										size="sm"
										onClick={() => (globalThis as any).window.open(candidate.linkedinUrl, '_blank')}
									>
										<ExternalLink className="mr-1 h-3 w-3" />
										LinkedIn
									</Button>
								)}
								{candidate.cvFileUrl && (
									<Badge variant="outline" className="text-xs">
										<FileText className="mr-1 h-3 w-3" />
										CV Uploaded
									</Badge>
								)}
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Hiring Requests */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Building2 className="h-5 w-5" />
						Hiring Requests ({hiringRequests.length})
					</CardTitle>
				</CardHeader>
				<CardContent>
					{hiringRequests.length === 0 ? (
						<p className="text-muted-foreground text-center py-8">
							No hiring requests associated with this candidate.
						</p>
					) : (
						<div className="space-y-3">
							{hiringRequests.map((entry: any, index: number) => (
								<div
									key={entry.sourcedCandidateId || index}
									className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
									onClick={() => {
										if (entry.hiringRequest?.id) {
											navigate(`/hiring-requests/${entry.hiringRequest.id}`);
										}
									}}
								>
									<div className="flex-1 min-w-0">
										<div className="font-medium">
											{entry.hiringRequest?.roleTitle || 'Unknown Role'}
										</div>
										{entry.hiringRequest?.company?.name && (
											<div className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
												<Building2 className="h-3 w-3" />
												{entry.hiringRequest.company.name}
											</div>
										)}
										{entry.hiringRequest?.location && (
											<div className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
												<MapPin className="h-3 w-3" />
												{entry.hiringRequest.location}
											</div>
										)}
									</div>
									<div className="flex flex-wrap items-center gap-2 shrink-0">
										{/* Candidate status in this hiring request */}
										{entry.candidateStatus === 'hired' ? (
											<Badge variant="success">Hired</Badge>
										) : entry.candidateStatus === 'withdrawn' ? (
											<Badge variant="destructive">Withdrawn</Badge>
										) : entry.recommended ? (
											<Badge variant="success" className="gap-1">
												<Star className="h-3 w-3" />
												Recommended
											</Badge>
										) : (
											<Badge variant={candidateStatusColors[entry.candidateStatus] as any || 'secondary'}>
												{formatStatus(entry.candidateStatus)}
											</Badge>
										)}
										{entry.cvFileUrl && (
											<Badge variant="outline" className="text-xs">
												<FileText className="mr-1 h-3 w-3" />
												CV
											</Badge>
										)}
										{/* Hiring request status */}
										<Badge
											variant={hiringRequestStatusColors[entry.hiringRequest?.status] as any || 'secondary'}
											className="text-xs"
										>
											HR: {formatStatus(entry.hiringRequest?.status || 'unknown')}
										</Badge>
									</div>
								</div>
							))}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
